import { useCallback, useState } from "react";

export enum LocalStorageKeys {
  ACCESS_TOKEN = "access_token",
  REFRESH_TOKEN = "refresh_token",
  TENANT_ID = "tenant_id",
  HOTEL_ID = "hotel_id",
  ADMIN_ACCESS_TOKEN = "adminAccessToken",
  ADMIN_REFRESH_TOKEN = "adminRefreshToken",
  ADMIN_TENANT_ID = "adminTenantId",
  ADMIN_HOTEL_ID = "adminHotelId",
}

export interface LocalStorage {
  accessToken: string | null;
  refreshToken: string | null;
  tenantId: string | null;
  hotelId: string | null;
  adminAccessToken: string | null;
  adminRefreshToken: string | null;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setTenantId: (tenantId: string | null) => void;
  setHotelId: (hotelId: string | null) => void;
  setAdminAccessToken: (token: string | null) => void;
  setAdminRefreshToken: (token: string | null) => void;
  clearTokens: () => void;
}

export const useLocalStorage = (): LocalStorage => {
  const [accessToken, setAccessTokenState] = useState<string | null>(
    localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN),
  );
  const [refreshToken, setRefreshTokenState] = useState<string | null>(
    localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN),
  );
  const [tenantId, setTenantIdState] = useState<string | null>(
    localStorage.getItem(LocalStorageKeys.TENANT_ID),
  );
  const [hotelId, setHotelIdState] = useState<string | null>(
    localStorage.getItem(LocalStorageKeys.HOTEL_ID),
  );
  const [adminAccessToken, setAdminAccessTokenState] = useState<string | null>(
    localStorage.getItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN),
  );
  const [adminRefreshToken, setAdminRefreshTokenState] = useState<
    string | null
  >(localStorage.getItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN));

  const clearLocalStorage = useCallback(() => {
    Object.values(LocalStorageKeys).forEach((key) => {
      localStorage.removeItem(key);
    });
    setAccessTokenState(null);
    setRefreshTokenState(null);
    setTenantIdState(null);
    setHotelIdState(null);
    setAdminAccessTokenState(null);
    setAdminRefreshTokenState(null);
  }, []);

  const setAccessToken = useCallback((value: string | null) => {
    if (value) {
      setAccessTokenState(value);
      localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, value);
      return;
    }

    setAccessTokenState(null);
    localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
  }, []);

  const setRefreshToken = useCallback((value: string | null) => {
    if (value) {
      setRefreshTokenState(value);
      localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, value);
      return;
    }

    setRefreshTokenState(null);
    localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
  }, []);

  const setTenantId = useCallback((value: string | null) => {
    if (value) {
      setTenantIdState(value);
      localStorage.setItem(LocalStorageKeys.TENANT_ID, value);
      return;
    }

    setTenantIdState(null);
    localStorage.removeItem(LocalStorageKeys.TENANT_ID);
  }, []);

  const setHotelId = useCallback((value: string | null) => {
    if (value) {
      setHotelIdState(value);
      localStorage.setItem(LocalStorageKeys.HOTEL_ID, value);
      return;
    }

    setHotelIdState(null);
    localStorage.removeItem(LocalStorageKeys.HOTEL_ID);
  }, []);

  const setAdminAccessToken = useCallback((value: string | null) => {
    if (value) {
      setAdminAccessTokenState(value);
      localStorage.setItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN, value);
      return;
    }

    setAdminAccessTokenState(null);
    localStorage.removeItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN);
  }, []);

  const setAdminRefreshToken = useCallback((value: string | null) => {
    if (value) {
      setAdminRefreshTokenState(value);
      localStorage.setItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN, value);
      return;
    }

    setAdminRefreshTokenState(null);
    localStorage.removeItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN);
  }, []);

  return {
    tenantId,
    hotelId,
    accessToken,
    refreshToken,
    adminAccessToken,
    adminRefreshToken,
    setAccessToken,
    setRefreshToken,
    setTenantId,
    setHotelId,
    setAdminAccessToken,
    setAdminRefreshToken,
    clearTokens: clearLocalStorage,
  };
};
